import { create } from "zustand";
import type { BeforeInstallPromptEvent } from "../utils/pwaUtils";
import { runInstallPrompt, markInstalled, suppressPrompt } from "../utils/pwaPromptState";
import { logPwaEvent } from "../utils/pwaEventTracking";
import {
  PWA_NATIVE_PROMPT_AVAILABLE,
  PWA_NATIVE_PROMPT_ACCEPTED,
  PWA_NATIVE_PROMPT_DISMISSED,
  PWA_SOURCE_NATIVE_PROMPT,
} from "../constants/pwaEvents";

type InstallResult = "accepted" | "dismissed" | "error" | "unavailable";

// Shared UI state for the PWA install flow so the centralised modal and the
// header "Install app" menu entry drive the same prompt and the single captured
// beforeinstallprompt event.
interface PwaInstallState {
  // Set once when Chromium fires beforeinstallprompt; null on iOS (never fires)
  // and after a completed/declined native prompt.
  deferredPrompt: BeforeInstallPromptEvent | null;
  isModalOpen: boolean;
  // Which surface opened the modal (e.g. auto-modal vs header), so the
  // pwa_install_modal_shown funnel event can attribute the open. null when closed.
  modalOpenSource: string | null;
  setDeferredPrompt: (deferredPrompt: BeforeInstallPromptEvent | null) => void;
  openModal: (source?: string) => void;
  closeModal: () => void;
  // Run the captured native prompt and reconcile the modal in one place.
  // beforeinstallprompt is single-use, so the prompt is always cleared
  // afterwards; on a dismissal we start the cooldown (so we don't nag) instead
  // of leaving a broken "Install" affordance that would reopen a dead-end modal.
  triggerInstall: () => Promise<InstallResult>;
}

const usePwaInstallStore = create<PwaInstallState>((set, get) => ({
  deferredPrompt: null,
  isModalOpen: false,
  modalOpenSource: null,
  setDeferredPrompt: (deferredPrompt) => {
    set({ deferredPrompt });
    // Funnel entry: Chromium fired beforeinstallprompt, so the site is
    // installable. Only a real prompt counts — clearing the spent prompt (null)
    // is not a funnel signal.
    if (deferredPrompt) logPwaEvent(PWA_NATIVE_PROMPT_AVAILABLE);
  },
  openModal: (source) => set({ isModalOpen: true, modalOpenSource: source ?? null }),
  closeModal: () => set({ isModalOpen: false, modalOpenSource: null }),
  triggerInstall: async () => {
    const { deferredPrompt } = get();
    // No captured prompt (iOS, or already spent) — callers should be showing
    // manual instructions instead of invoking this.
    if (!deferredPrompt) return "unavailable";

    const outcome = await runInstallPrompt(deferredPrompt);
    // The prompt is single-use; clear it and close the modal regardless of
    // outcome so nothing keeps advertising an install that can't fire again.
    set({ deferredPrompt: null, isModalOpen: false });

    if (outcome === "accepted") {
      markInstalled();
      logPwaEvent(PWA_NATIVE_PROMPT_ACCEPTED, { source: PWA_SOURCE_NATIVE_PROMPT });
    } else if (outcome === "dismissed") {
      // User declined the native dialog — respect it like a "Later".
      suppressPrompt();
      logPwaEvent(PWA_NATIVE_PROMPT_DISMISSED, { source: PWA_SOURCE_NATIVE_PROMPT });
    }
    return outcome;
  },
}));

export default usePwaInstallStore;
